import {
  ArrowRed,
  Box,
  Chevron,
  Disclaimer,
  Div,
  H5,
  Icon,
  ModalPortal,
  P,
  Pill,
  SaasEligibleLocation,
  Tab,
  TooltipIcon,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { useState } from 'react';
import { SaasEligibleDetailsModal } from './SaasEligibleDetailsModal';
import {
  SAAS_ELIGIBLE_SERVICES,
  SaasEligibleService,
  openLocationInNewTab,
  qualifyingLabel,
} from './saasEligibility';
import { useSaasEligibleLocations } from './useSaasEligibleLocations';

const SUMMARY_SIZE = 5;

const TITLE = 'SaaS Eligible Locations';

const TITLE_TOOLTIP =
  'Locations that pay no SaaS fee, where vGrubs brought at least $200 in vDrive savings, $300 in virtual brand sales or $100 in recovered orders, or over $200 of the three together. Always the last 30 days, whatever dates are picked above.';

type SortKey = 'total' | SaasEligibleService;

const SORTS: { key: SortKey; label: string }[] = [
  { key: 'total', label: 'Total' },
  ...SAAS_ELIGIBLE_SERVICES.map(({ key, short }) => ({ key, label: short })),
];

/**
 * Next to the brand rankings: locations that pay no SaaS fee but would
 * qualify for one, by what vGrubs brought them over the last 30 days. The
 * card shows the top 5 in the backend's order (total, highest first); the
 * arrow opens the whole list, which can be re-sorted by service. A row opens
 * its breakdown; the name opens the location in a new tab.
 */
export const SaasEligibleLocations = () => {
  const { locations, isLoading, isError } = useSaasEligibleLocations();
  const [listOpen, setListOpen] = useState(false);
  const [sort, setSort] = useState<SortKey>('total');
  const [selected, setSelected] = useState<SaasEligibleLocation | null>(null);

  // A copy: the backend's order is what the card shows.
  const sorted =
    sort === 'total'
      ? locations
      : [...locations].sort((a, b) => b[sort] - a[sort]);

  return (
    <>
      <Box
        $p="16px 20px"
        $relative
        $overflow="hidden"
        $height="100%"
        $flex
        $column
      >
        {locations.length > 0 && (
          <Icon
            $pointer
            style={{ position: 'absolute', right: 16, top: 16 }}
            onClick={() => setListOpen(true)}
            color={lightTheme.lightGray}
          >
            <ArrowRed />
          </Icon>
        )}
        {/* "Last 30 days" shares the title's line: on a line of its own it
            pushed the rows below the brand rankings beside this card, and
            their separators stopped lining up. Clear of the arrow. */}
        <Div $flex $alignCenter $gap="6px" $mb="16" $wrap $p="0 32px 0 0">
          <H5 $m="0">{TITLE}</H5>
          <TooltipIcon tooltip={TITLE_TOOLTIP} />
          {/* The compare pill's look (renderPercentageChange), in gray. The
              pill is 24px against the title's 22px line, so it gives back
              1px each side to keep the rows level with the brand cards. */}
          <Pill
            $background={`${lightTheme.lightGray}26`}
            style={{ margin: '-1px 0 -1px 2px' }}
          >
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              Last 30 days
            </Disclaimer>
          </Pill>
        </Div>

        {/* Same body as the brand lists beside it: a 5-row floor, so the
            empty placeholder centers instead of pinning to the top. */}
        <Div $minHeight="350px" $flex $column style={{ flex: 1 }}>
          {locations.slice(0, SUMMARY_SIZE).map((item, index) => (
            <EligibleRow
              key={item.locationId}
              item={item}
              index={index}
              onOpen={() => setSelected(item)}
              truncate
            />
          ))}

          {locations.length === 0 && !isLoading && (
            <Div $flex $center style={{ flex: 1 }}>
              <P $m="0" $center>
                {isError
                  ? 'Could not load this list.'
                  : 'No locations without a SaaS fee qualify in the last 30 days.'}
              </P>
            </Div>
          )}
        </Div>
      </Box>

      <ModalPortal
        isOpen={listOpen}
        closeModal={() => setListOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="560px"
        maxHeight="calc(65px * 10)"
        modalOverflow="auto"
        titleWithExit={TITLE}
      >
        <Div $flex $alignCenter $gap="8px" $mb="8" $wrap>
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Sort by
          </Disclaimer>
          {SORTS.map(({ key, label }) => (
            <Tab
              key={key}
              $small
              selected={key === sort}
              onClick={() => setSort(key)}
              // Inline: the design system's media-queried min-width would
              // clamp each pill to 100-150px otherwise.
              style={{ minWidth: 'auto', padding: '0 12px' }}
            >
              {label}
            </Tab>
          ))}
        </Div>
        {sorted.map((item, index) => (
          <EligibleRow
            key={item.locationId}
            item={item}
            index={index}
            onOpen={() => setSelected(item)}
            sortedBy={sort === 'total' ? undefined : sort}
          />
        ))}
      </ModalPortal>

      <SaasEligibleDetailsModal
        item={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
};

type RowProps = {
  item: SaasEligibleLocation;
  index: number;
  onOpen: () => void;
  /** The service the list is sorted by: its amount goes under the total. */
  sortedBy?: SaasEligibleService;
  /**
   * On the card: exactly as tall as a brand ranking row, so the separators
   * line up with the cards beside it, with a long reason cut short ("...")
   * and whole on hover. The full list lets it wrap instead.
   */
  truncate?: boolean;
};

/**
 * Rank and name (a link to the location), owner and why it is listed, the
 * total, and a chevron: the rest of the row opens the details.
 */
const EligibleRow = ({
  item,
  index,
  onOpen,
  sortedBy,
  truncate = false,
}: RowProps) => {
  const reason = [item.ownerName, qualifyingLabel(item)]
    .filter(Boolean)
    .join(' · ');
  return (
    <Div
      $flex
      $alignCenter
      $between
      $borderBottom
      $borderColor={lightTheme.lightGray}
      // As tall as a brand ranking row; in the full list taller when the
      // reason wraps.
      $height={truncate ? '70px' : undefined}
      $heightSM={truncate ? '70px' : undefined}
      $minHeight="70px"
      $minHeightSM="70px"
      $p={truncate ? undefined : '10px 0'}
      $gap="12px"
      $pointer
      onClick={onOpen}
    >
      <Div $minWidth="0">
        {/* Only as wide as the name, so the underline and the new tab
          stay on the name and the rest of the row opens the details. */}
        <P
          $bold
          $mb="4"
          $left
          $lineHeight="1.2"
          $pointer
          $hoverUnderline
          style={{ width: 'fit-content' }}
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            openLocationInNewTab(item.locationId);
          }}
        >
          {index + 1}. {item.locationName}
        </P>
        <Disclaimer
          $m="0"
          $left
          $treeDots={truncate}
          title={truncate ? reason : undefined}
        >
          {reason}
        </Disclaimer>
      </Div>
      <Div $flex $alignCenter $gap="12px" $flexNone>
        <Div $flex $column $alignEnd>
          <P $bold $m="0" $color={lightTheme.green}>
            {displayCentsInDollars(item.total)}
          </P>
          {sortedBy && (
            <Disclaimer $m="0" $right $color={lightTheme.lightGray}>
              {serviceShort(sortedBy)} {displayCentsInDollars(item[sortedBy])}
            </Disclaimer>
          )}
        </Div>
        {/* The row opens the details: Chevron points up, turned to the right. */}
        <Icon $rotate="90deg" $size="14" color={lightTheme.lightGray}>
          <Chevron />
        </Icon>
      </Div>
    </Div>
  );
};

const serviceShort = (key: SaasEligibleService) =>
  SAAS_ELIGIBLE_SERVICES.find((service) => service.key === key)?.short ?? '';
